import { useRef, useEffect, useMemo } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { X, Volume2 } from "lucide-react";
import {
  formatBytes,
  formatCalendarDate,
  formatTimeFromRecording,
  extractTimestampFromRecording,
} from "./utils";
import type { RecordingTimelineProps, AudioRecording } from "./types";
import { useSidebar } from "../ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";

const recordingDateKey = (recording: AudioRecording): string => {
  const match = recording.key.match(/audio\/(\d{4})\/(\d{2})\/(\d{2})\//);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const ts = extractTimestampFromRecording(recording);
  if (!ts) return "unknown";
  return `${ts.getUTCFullYear()}-${String(ts.getUTCMonth() + 1).padStart(2, "0")}-${String(ts.getUTCDate()).padStart(2, "0")}`;
};

export const RecordingTimeline = ({
  recordings,
  selectedSession,
  currentPlayingRecording,
  loading,
  isOpen,
  height,
  onClose,
  onHeightChange,
  onPlayRecording,
}: RecordingTimelineProps) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizing.current) return;
    const windowHeight = window.innerHeight;
    const newHeight = windowHeight - e.clientY;
    // Constrain between min and max heights
    const constrainedHeight = Math.min(
      Math.max(newHeight, 180),
      windowHeight * 0.7,
    );
    onHeightChange(constrainedHeight);
  };

  const handleResizeEnd = () => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleResizeMove);
    document.removeEventListener("mouseup", handleResizeEnd);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };
  }, []);

  const { state } = useSidebar();
  const isMobile = useIsMobile();

  const groupedByDate = useMemo(() => {
    const groups = new Map<
      string,
      Array<{ recording: AudioRecording; globalIndex: number }>
    >();
    recordings.forEach((recording, globalIndex) => {
      const key = recordingDateKey(recording);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push({ recording, globalIndex });
    });
    // Sort each group by timestamp ascending (already true, defensive)
    for (const list of groups.values()) {
      list.sort((a, b) => a.globalIndex - b.globalIndex);
    }
    // Newest day first
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [recordings]);

  if (!selectedSession || !isOpen) return null;

  const sidebarWidth = isMobile
    ? 0
    : state === "collapsed"
      ? "var(--sidebar-width-icon)"
      : "var(--sidebar-width)";

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 bg-card border-t flex flex-col min-h-[180px] max-h-[50vh] transition-[left] duration-200 ease-linear"
      ref={timelineRef}
      style={{ height: `${height}px`, left: sidebarWidth }}
    >
      {/* Resize handle */}
      <div
        className="h-2 bg-muted cursor-ns-resize flex items-center justify-center shrink-0 hover:bg-muted/80 transition-colors"
        onMouseDown={handleResizeStart}
      >
        <div className="w-8 h-0.5 bg-muted-foreground/30 rounded-full" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div>
          <h3 className="text-sm font-medium flex items-center gap-2">
            Recording Timeline
            {selectedSession.lesson_name && (
              <Badge variant="secondary" className="text-xs font-normal">
                {selectedSession.lesson_name}
              </Badge>
            )}
            {selectedSession.lesson_id && (
              <Badge variant="outline" className="text-xs font-mono">
                Lesson #{selectedSession.lesson_id}
              </Badge>
            )}
            {selectedSession.slide_index != null && (
              <Badge variant="outline" className="text-xs font-mono">
                Slide {selectedSession.slide_index}
              </Badge>
            )}
          </h3>
          <p className="text-xs text-muted-foreground">
            {recordings.length} recordings · {groupedByDate.length} day
            {groupedByDate.length === 1 ? "" : "s"}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={onClose}
          title="Close timeline"
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 px-4">
        {loading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="size-5 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin mr-3" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : recordings.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Volume2 className="size-6 mb-2" />
            <p className="text-sm">No recordings found</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 pb-2">
            {groupedByDate.map(([dateKey, items]) => (
              <div key={dateKey} className="flex flex-col">
                <div className="flex items-baseline gap-2 mb-2 sticky top-0 bg-card/95 backdrop-blur-sm py-1 z-10 border-b border-border/40">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {formatCalendarDate(dateKey)}
                  </span>
                  <Badge variant="secondary" className="text-[0.65rem]">
                    {items.length}
                  </Badge>
                </div>
                <div className="overflow-x-auto">
                  <div className="relative flex gap-3 pb-6 min-w-max">
                    <div className="absolute bottom-3 left-4 right-4 h-px bg-border" />
                    {items.map(({ recording, globalIndex }) => {
                      const isCurrentlyPlaying =
                        currentPlayingRecording?.key === recording.key;
                      return (
                        <div
                          key={recording.key}
                          className="relative flex flex-col items-center cursor-pointer min-w-[70px] max-w-[90px] group"
                          onClick={() => onPlayRecording(recording)}
                        >
                          <Card
                            className={`w-full text-center transition-colors mb-3 ${
                              isCurrentlyPlaying
                                ? "border-primary bg-accent"
                                : "hover:bg-muted/50"
                            }`}
                          >
                            <CardContent className="p-2.5">
                              <div
                                className={`text-lg font-bold leading-none mb-1 ${isCurrentlyPlaying ? "text-primary" : ""}`}
                              >
                                {globalIndex + 1}
                              </div>
                              <div className="text-[0.65rem] text-muted-foreground font-mono leading-tight mb-0.5">
                                {formatTimeFromRecording(recording)}
                              </div>
                              <div className="text-[0.6rem] text-muted-foreground/70">
                                {formatBytes(recording.size_bytes)}
                              </div>
                              {recording.slide_index != null && (
                                <div className="text-[0.6rem] font-mono text-muted-foreground mt-0.5 pt-0.5 border-t border-border/40">
                                  Bite {recording.slide_index}
                                </div>
                              )}
                              {recording.surface === "NOTEPAD" && (
                                <div className="text-[0.6rem] text-muted-foreground mt-0.5">
                                  Notepad
                                </div>
                              )}
                              {recording.partial && (
                                <div
                                  className="text-[0.6rem] font-medium text-amber-700 mt-0.5"
                                  title="Not the whole turn: the stream ended before the turn finished, or the turn was too long to keep"
                                >
                                  Partial
                                </div>
                              )}
                            </CardContent>
                          </Card>
                          <div
                            className={`size-2 rounded-full relative z-10 ${
                              isCurrentlyPlaying
                                ? "bg-primary"
                                : "bg-muted-foreground/40"
                            }`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
